<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="onFilter">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="Y">{{ $t('attributes.shelve_on') }}</option>
              <option value="N">{{ $t('attributes.shelve_off') }}</option>
            </b-select>
          </b-field>
          <b-field>
            <b-select
              v-model="selectedAreaCode"
              :loading="loadingAreas"
              :placeholder="$t('message.please_select') + $t('attributes.area')"
              @input="onSelectArea"
            >
              <option
                v-for="option in areas"
                :value="option.code"
                :key="option.code"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
        </b-field>
      </div>
      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="isCreateModalActive = true"
          >
            {{ $t('attributes.add') + $t('attributes.group') }}
          </b-button>
        </div>
      </div>
    </nav>
    <b-table
      :data="items"
      :loading="loadingItems"
      :selected.sync="selectedItem"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :current-page="page"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
      @select="onSelect"
    >
      <b-table-column
        field="region_list_sort"
        :label="$t('attributes.sort')"
        centered
        width="100"
        v-slot="props"
      >
        {{ props.row.region_list_sort }}
      </b-table-column>
      <b-table-column
        field="region_list_name"
        :label="$t('attributes.status')"
        centered
        width="100"
        v-slot="props"
      >
        <b-tag
          :type="props.row.status ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ shelveStatus(props.row.status) }}
        </b-tag>
      </b-table-column>
      <b-table-column
        field="area_name"
        :label="$t('attributes.area')"
        centered
        width="160"
        v-slot="props"
      >
        {{ props.row.area_name }}
      </b-table-column>
      <b-table-column
        field="region_list_name"
        :label="$t('attributes.group') + $t('attributes.name')"
        width="250"
        v-slot="props"
      >
        {{ props.row.region_list_name }}
      </b-table-column>
      <b-table-column
        field="catalogs"
        :label="$t('attributes.group') + $t('attributes.catalog')"
        v-slot="props"
      >
        <span
          v-for="(item, index) in props.row.region_list_catalogs"
          :key="index"
        >
          {{ item.region_list_catalog_name }}
          <span v-if="index < props.row.region_list_catalogs.length - 1">
            ,
          </span>
        </span>
      </b-table-column>
      <b-table-column
        :label="$t('attributes.function')"
        width="80"
        centered
        v-slot="props"
      >
        <div class="buttons is-center">
          <b-button type="is-info" @click.prevent.stop="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
        </div>
      </b-table-column>
      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
            <p v-if="selectedAreaCode">{{ $t('attributes.empty') }}</p>
            <p v-else>請選擇線別</p>
          </div>
        </section>
      </template>
    </b-table>
    <section style="min-height: 100px;">
      <div style="display: inline-block; font-size: 18px; font-weight: 600;">
        未加入群組類別：
      </div>
      <div style="display: inline-block;">
        <b-tag
          type="is-light"
          v-for="(item, index) in catalogWithoutRegionList"
          :key="index"
          style="font-size: 16px; margin: 10px 5px;"
        >
          {{ item.name }}
          {{ !item.status ? '(下架)' : '' }}
        </b-tag>
      </div>
    </section>
    <section id="item" style="min-height: 600px; margin-top:20px;">
      <hr />
      <area-region-catalog-list
        v-if="selectedAreaCode && selectedItem"
        :region-no="selectedItem.region_list_no"
        :region-name="selectedItem.region_list_name"
        :area-code="selectedAreaCode"
        @updated="onRegionCatalogsUpdate"
      ></area-region-catalog-list>
    </section>
    <b-modal
      :active.sync="isCreateModalActive"
      :can-cancel="['x']"
      scroll="keep"
      has-modal-card
    >
      <area-region-create-form
        :areas="areas"
        @onSend="onCreate"
      ></area-region-create-form>
    </b-modal>
    <b-modal
      :active.sync="isModifyModalActive"
      :can-cancel="['x']"
      has-modal-card
    >
      <area-region-form :region="selectedItem" @onSend="onUpdate" />
    </b-modal>
  </section>
</template>

<script>
import { AREA_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'
import AreaRegionForm from '@/components/areas/AreaRegionForm'
import AreaRegionCreateForm from '@/components/areas/AreaRegionCreateForm'
import AreaRegionCatalogList from '@/components/areas/AreaRegionCatalogList'

export default {
  name: 'AreaRegions',
  components: { AreaRegionCatalogList, AreaRegionForm, AreaRegionCreateForm },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,
      catalogWithoutRegionList: [],

      filterStatus: '',
      sortField: 'sort',
      sortOrder: 'asc',
      defaultSortOrder: 'asc',
      page: 1,
      perPage: 10,

      loadingAreas: false,
      areas: [],
      selectedArea: null,
      selectedAreaCode: null,

      isCreateModalActive: false,
      sending: false,

      isModifyModalActive: false
    }
  },
  watch: {
    selected(item) {
      if (item) {
        this.items = this.items.map(x => {
          if (x.region_list_no === item.region_list_no) {
            return item
          } else {
            return x
          }
        })
      }
      this.selectedItem = item
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        this.loadingAreas = true
        const api = `${AREA_API}/list`
        const res = await this.axios.get(api)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.selectedArea = null

        if (this.selectedAreaCode) {
          this.selectedArea = areas.find(a => a.code === this.selectedAreaCode)
          this.selectedAreaCode = this.selectedArea
            ? this.selectedArea.code
            : null
        }
        this.onSelectArea(this.selectedAreaCode)
      } catch (e) {
        this.areas = []
        this.selectedArea = null
        this.selectedAreaCode = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadCatalogsWithoutRegionList() {
      if (this.selectedAreaCode === null) return false
      try {
        this.loadingItems = true
        const api = `${AREA_API}/${this.selectedAreaCode}/region/catalogs/withoutRegionList`
        const res = await this.axios.get(api)
        this.catalogWithoutRegionList = res.data.data
      } catch (e) {
        this.catalogWithoutRegionList = []
        notifyService.fetch_error('region', e)
      } finally {
        this.loadingItems = false
      }
    },
    async loadItems() {
      if (this.selectedAreaCode === null) return false
      try {
        const params = {
          status: this.filterStatus,
          area_code: this.selectedAreaCode,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const api = `${AREA_API}/region/list`
        const res = await this.axios.get(api, { params })
        this.items = res.data.data.items
        this.total = res.data.data.item_count

        await this.loadCatalogsWithoutRegionList()
      } catch (e) {
        this.items = []
        this.total = 0
        this.onSelect(null)
        notifyService.fetch_error('region', e)
      } finally {
        this.loadingItems = false
      }
    },
    onFilter: debounce(function() {
      this.page = 1
      this.onSelect(null)
      this.loadItems()
    }, 500),
    onSelectArea(code) {
      this.page = 1
      if (code) {
        this.selectedArea = this.areas.find(a => a.code === code)
        this.selectedAreaCode = this.selectedArea
          ? this.selectedArea.code
          : null
      }

      this.onSelect(null)
      this.loadItems()
    },
    onPageChange(page) {
      this.page = page
      this.onSelect(null)
      this.loadItems()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
    },
    onSelect(item) {
      this.selectedItem = item

      if (item !== null) {
        this.$scrollTo(`#item`, { offset: -60 })
      }
    },
    onModify(item) {
      this.selectedItem = item
      this.isModifyModalActive = true
    },
    async onUpdate(item) {
      const api = `${AREA_API}/region/update/basic`
      try {
        this.loadingItems = true
        await this.axios.put(api, item)

        notifyService.success(`${this.$t('attributes.update_succeed')}`)
        this.isModifyModalActive = false
        await this.loadItems()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.loadingItems = false
      }
    },
    async onCreate(item) {
      try {
        const api = `${AREA_API}/region/create`

        this.loadingItems = true
        await this.axios.post(api, item)

        this.isCreateModalActive = false

        notifyService.success(this.$t('attributes.add_succeed'))
        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.loadingItems = false
      }
    },
    onRegionCatalogsUpdate(result) {
      if (result.length > 0) {
        this.items.forEach(item => {
          if (item.region_list_no === this.selectedItem.region_list_no) {
            item.region_list_catalogs = result
            return false
          }
        })
      }
    }
  }
}
</script>
